import { create } from 'zustand';
import {
  loginRequest,
  logoutRequest,
  meRequest,
  refreshRequest,
  type MePayload,
  type TokenPair,
} from './api';
import { registerAuthHooks } from '../../shared/http/client';
import {
  clearRefreshToken,
  readRefreshToken,
  saveRefreshToken,
} from '../../shared/http/refresh-storage';

export type AuthStatus = 'idle' | 'bootstrapping' | 'anonymous' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: MePayload | null;
  accessToken: string | null;
  accessExpiresAt: Date | null;
  tenantSlug: string | null;
  bootstrap: () => Promise<void>;
  login: (input: { email: string; password: string; tenantSlug?: string | null }) => Promise<void>;
  /** Autentica a partir de um TokenPair obtido via SSO Leddo (callback OAuth). */
  loginWithLeddoTokens: (tokens: TokenPair) => Promise<void>;
  logout: () => Promise<void>;
  tryRefreshSilently: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'idle',
  user: null,
  accessToken: null,
  accessExpiresAt: null,
  tenantSlug: null,

  bootstrap: async () => {
    if (get().status === 'bootstrapping') return;
    set({ status: 'bootstrapping' });
    const refresh = readRefreshToken();
    if (refresh === null || refresh.length === 0) {
      set({ status: 'anonymous' });
      return;
    }
    try {
      const tokens = await refreshRequest({ refresh_token: refresh });
      saveRefreshToken(tokens.refresh_token);
      set({
        accessToken: tokens.access_token,
        accessExpiresAt: new Date(tokens.access_expires_at),
      });
      const me = await meRequest();
      set({
        user: me,
        tenantSlug: me.tenant_slug,
        status: 'authenticated',
      });
    } catch {
      clearRefreshToken();
      set({
        accessToken: null,
        accessExpiresAt: null,
        user: null,
        tenantSlug: null,
        status: 'anonymous',
      });
    }
  },

  login: async ({ email, password, tenantSlug }) => {
    const tokens = await loginRequest({
      email,
      password,
      tenant_slug:
        tenantSlug !== undefined && tenantSlug !== null && tenantSlug.length > 0
          ? tenantSlug
          : null,
    });
    saveRefreshToken(tokens.refresh_token);
    set({
      accessToken: tokens.access_token,
      accessExpiresAt: new Date(tokens.access_expires_at),
    });
    const me = await meRequest();
    set({
      user: me,
      tenantSlug: me.tenant_slug,
      status: 'authenticated',
    });
  },

  loginWithLeddoTokens: async (tokens: TokenPair) => {
    saveRefreshToken(tokens.refresh_token);
    set({
      accessToken: tokens.access_token,
      accessExpiresAt: new Date(tokens.access_expires_at),
    });
    const me = await meRequest();
    set({
      user: me,
      tenantSlug: me.tenant_slug,
      status: 'authenticated',
    });
  },

  logout: async () => {
    try {
      if (get().accessToken !== null) {
        await logoutRequest();
      }
    } catch {
      /* noop */
    }
    clearRefreshToken();
    set({
      user: null,
      accessToken: null,
      accessExpiresAt: null,
      tenantSlug: null,
      status: 'anonymous',
    });
  },

  tryRefreshSilently: async () => {
    const refresh = readRefreshToken();
    if (refresh === null || refresh.length === 0) return false;
    try {
      const tokens = await refreshRequest({ refresh_token: refresh });
      saveRefreshToken(tokens.refresh_token);
      set({
        accessToken: tokens.access_token,
        accessExpiresAt: new Date(tokens.access_expires_at),
      });
      return true;
    } catch {
      return false;
    }
  },
}));

registerAuthHooks({
  getAccessToken: () => useAuthStore.getState().accessToken,
  tryRefreshSilently: () => useAuthStore.getState().tryRefreshSilently(),
  onUnauthorized: () => {
    void useAuthStore.getState().logout();
  },
});
